import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { MapPin, Clock, Briefcase, ArrowLeft, Upload, Trash } from 'lucide-react';
import { applyJob } from '../services/Job';
import { jobMediaService } from '../services/Job';
import { API_ORIGIN } from '../services/apiConfig';
import { toast } from 'sonner';
import { SEOHead } from './SEOHead';
import { useSiteSettings } from '../services/SiteSettings';

interface Job {
  id: number;
  title: string;
  department: string;
  location: string;
  type: string;
  description: string;
  qualifications?: string[];
  education?: string;
  responsibilities?: string[];
  createdDate: string;
  subThematicArea?: string;
  assignmentType?: string;
  subAssignmentType?: string;
}

interface JobDetailsPageProps {
  job: Job;
  onBack: () => void;
}

export function JobDetailsPage({ job, onBack }: JobDetailsPageProps) {
  const { taxonomy } = useSiteSettings();
  const THEMATIC_AREAS = taxonomy.thematicAreas.map((a) => a.name);
  const SUB_THEMATIC_AREAS: Record<string, string[]> = Object.fromEntries(
    taxonomy.thematicAreas.map((a) => [a.name, a.subAreas || []])
  );
  const ASSIGNMENT_TYPES = taxonomy.assignmentTypes.map((t) => t.name);
  const SUB_ASSIGNMENT_TYPES: Record<string, string[]> = Object.fromEntries(
    taxonomy.assignmentTypes.map((t) => [t.name, t.subTypes || []])
  );
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    coverLetter: '',
    yearsOfExperience: '',
    education: '',
    expectedPayPerDay: '',
    expectedPayPerMonth: '',
    numbersOfProjectsWorked: '',
    resumeUrl: '',
    // New fields for thematic areas and assignments
    selectedThematicAreas: [] as string[],
    selectedSubThematicAreas: [] as string[],
    selectedAssignmentTypes: [] as string[],
    selectedSubAssignmentTypes: [] as string[],
  });

  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [applyLoading, setApplyLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [resumePreview, setResumePreview] = useState<string | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

  // Check if job is Field Researcher position
  const isFieldResearcher = job.title.includes('Field Researcher');

  const formatPostedDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    }
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Handle thematic area selection
  const handleThematicAreaChange = (area: string) => {
    setFormData(prev => {
      const currentAreas = [...prev.selectedThematicAreas];
      if (currentAreas.includes(area)) {
        // Remove if already selected
        const updatedAreas = currentAreas.filter(a => a !== area);
        // Also remove related sub-thematic areas
        const updatedSubAreas = prev.selectedSubThematicAreas.filter(
          subArea => !SUB_THEMATIC_AREAS[area]?.includes(subArea)
        );
        return {
          ...prev,
          selectedThematicAreas: updatedAreas,
          selectedSubThematicAreas: updatedSubAreas,
        };
      } else {
        // Add without restriction
        return {
          ...prev,
          selectedThematicAreas: [...currentAreas, area],
        };
      }
    });
  };

  // Handle sub-thematic area selection
  const handleSubThematicAreaChange = (subArea: string) => {
    setFormData(prev => {
      const currentSubAreas = [...prev.selectedSubThematicAreas];
      if (currentSubAreas.includes(subArea)) {
        return {
          ...prev,
          selectedSubThematicAreas: currentSubAreas.filter(a => a !== subArea),
        };
      } else {
        return {
          ...prev,
          selectedSubThematicAreas: [...currentSubAreas, subArea],
        };
      }
    });
  };

  // Handle assignment type selection
  const handleAssignmentTypeChange = (type: string) => {
    setFormData(prev => {
      const currentTypes = [...prev.selectedAssignmentTypes];
      if (currentTypes.includes(type)) {
        // Remove if already selected
        const updatedTypes = currentTypes.filter(t => t !== type);
        // Also remove related sub-assignment types
        const updatedSubTypes = prev.selectedSubAssignmentTypes.filter(
          subType => !SUB_ASSIGNMENT_TYPES[type]?.includes(subType)
        );
        return {
          ...prev,
          selectedAssignmentTypes: updatedTypes,
          selectedSubAssignmentTypes: updatedSubTypes,
        };
      } else {
        // Add without restriction
        return {
          ...prev,
          selectedAssignmentTypes: [...currentTypes, type],
        };
      }
    });
  };

  // Handle sub-assignment type selection
  const handleSubAssignmentTypeChange = (subType: string) => {
    setFormData(prev => {
      const currentSubTypes = [...prev.selectedSubAssignmentTypes];
      if (currentSubTypes.includes(subType)) {
        return {
          ...prev,
          selectedSubAssignmentTypes: currentSubTypes.filter(t => t !== subType),
        };
      } else {
        return {
          ...prev,
          selectedSubAssignmentTypes: [...currentSubTypes, subType],
        };
      }
    });
  };

  const handleResumeUpload = async (file: File) => {
    setUploading(true);
    try {
      const response = await jobMediaService.uploadMedia(
        [{ file }],
        { name: 'job-applications' }
      );

      if (response.uploadedAssets && response.uploadedAssets.length > 0) {
        const uploadedFile = response.uploadedAssets[0];
        const fileUrl = uploadedFile.src || `${API_ORIGIN}/usp/assets/view/${uploadedFile.fileName}`;

        setFormData(prev => ({ ...prev, resumeUrl: fileUrl }));
        setUploadedFileName(uploadedFile.fileName);
        toast.success("Resume uploaded successfully!");
        return { fileUrl, fileName: uploadedFile.fileName };
      } else {
        throw new Error("No uploaded assets returned");
      }
    } catch (error: any) {
      toast.error(`Resume upload failed: ${error.message}`);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteResume = async () => {
    if (!uploadedFileName) {
      removeResume();
      return;
    }

    try {
      await jobMediaService.deleteMedia(uploadedFileName);
      toast.success("Resume deleted successfully!");
      removeResume();
    } catch (error: any) {
      toast.error(`Resume deletion failed: ${error.message}`);
    }
  };

  const removeResume = () => {
    setFormData(prev => ({ ...prev, resumeUrl: "" }));
    setResumePreview(null);
    setResumeFile(null);
    setUploadedFileName(null);

    if (resumePreview) {
      URL.revokeObjectURL(resumePreview);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['.pdf', '.doc', '.docx'];
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

      if (!allowedTypes.includes(fileExtension || '')) {
        toast.error("Please select a PDF, DOC, or DOCX file");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size should be less than 5MB");
        return;
      }

      setResumeFile(file);

      // Create preview for PDF (you can show a PDF icon or file name)
      const previewUrl = URL.createObjectURL(file);
      setResumePreview(previewUrl);

      handleResumeUpload(file).catch(() => {
        setResumePreview(null);
        setResumeFile(null);
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.resumeUrl) {
      toast.error('Please upload your resume');
      return;
    }

    try {
      setApplyLoading(true);

      // Prepare payload - map phone to mobileNumber for API
      const payload = {
        ...formData,
        job_id: job.id.toString(),
        mobileNumber: formData.phone, // Map phone to mobileNumber
        // Remove phone from payload since API expects mobileNumber
        phone: undefined,
        // Include thematic areas and assignments only for NON-Field Researcher positions
        ...(!isFieldResearcher && {
          selectedThematicAreas: formData.selectedThematicAreas,
          selectedSubThematicAreas: formData.selectedSubThematicAreas,
          selectedAssignmentTypes: formData.selectedAssignmentTypes,
          selectedSubAssignmentTypes: formData.selectedSubAssignmentTypes,
        }),
      };

      // Remove the phone field from the payload
      delete payload.phone;

      await applyJob(job.id, payload);

      toast.success(
        'Application submitted successfully! We will review your application and get back to you soon.'
      );

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        coverLetter: '',
        yearsOfExperience: '',
        education: '',
        expectedPayPerDay: '',
        expectedPayPerMonth: '',
        numbersOfProjectsWorked: '',
        resumeUrl: '',
        selectedThematicAreas: [],
        selectedSubThematicAreas: [],
        selectedAssignmentTypes: [],
        selectedSubAssignmentTypes: [],
      });
      setResumeFile(null);
      setResumePreview(null);
      setUploadedFileName(null);

      const fileInput = document.getElementById('resume') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (error: any) {
      console.error('Error applying for job:', error);
      toast.error(error.message || 'Failed to submit application. Please try again.');
    } finally {
      setApplyLoading(false);
    }
  };

  const getDepartmentColor = (department: string) => {
    switch (department) {
      case 'Research':
        return 'bg-green-100 text-green-800';
      case 'Technical':
        return 'bg-blue-100 text-blue-800';
      case 'Operations':
        return 'bg-orange-100 text-orange-800';
      case 'Management':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const responsibilities = Array.isArray(job.responsibilities) ? job.responsibilities : [];
  const qualifications = Array.isArray(job.qualifications) ? job.qualifications : [];

  const jobSlug = job.title
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]/g, '')
    .toLowerCase();

  const jobPostingSchema = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": job.title,
    "description": job.description || `${job.title} position at USP Consultancy`,
    "datePosted": job.createdDate,
    "employmentType": job.type || "FULL_TIME",
    "hiringOrganization": {
      "@type": "Organization",
      "name": "USP Consultancy",
      "sameAs": "https://uspconsultancy.com.np/",
      "logo": "https://uspconsultancy.com.np/usp.png"
    },
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": job.location || "Kathmandu",
        "addressCountry": "NP"
      }
    },
    ...(job.education && { "educationRequirements": job.education }),
    ...(responsibilities.length > 0 && { "responsibilities": responsibilities.join(', ') }),
    ...(qualifications.length > 0 && { "qualifications": qualifications.join(', ') })
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://uspconsultancy.com.np/" },
      { "@type": "ListItem", "position": 2, "name": "Careers", "item": "https://uspconsultancy.com.np/careers" },
      { "@type": "ListItem", "position": 3, "name": job.title, "item": `https://uspconsultancy.com.np/job/${jobSlug}-${job.id}` }
    ]
  };

  return (
    <div className="min-h-screen py-16">
      <SEOHead
        title={`${job.title} - Career Opportunity | USP Consultancy Nepal`}
        description={`Apply for ${job.title} at USP Consultancy, ${job.location || 'Kathmandu'}. ${job.department ? `Department: ${job.department}.` : ''} ${job.type || 'Full-time'} position. ${job.description ? job.description.substring(0, 150) : 'Join our team of expert consultants working on development projects across Nepal.'}`}
        canonical={`https://uspconsultancy.com.np/job/${jobSlug}-${job.id}`}
        keywords={`${job.title}, job vacancy Nepal, ${job.department || 'consulting'} jobs, USP Consultancy careers, ${job.location || 'Kathmandu'} jobs, development sector jobs Nepal, consulting career opportunities`}
        ogType="article"
        structuredData={[jobPostingSchema, breadcrumbSchema]}
        publishedTime={job.createdDate}
        modifiedTime={new Date().toISOString()}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button variant="ghost" onClick={onBack} className="mb-8 cursor-pointer">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Careers
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Job Details */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h1 className="text-3xl font-bold mb-4">{job.title}</h1>
              {/* <div className="flex flex-wrap gap-2 mb-4">
                <Badge className={getDepartmentColor(job.department)}>{job.department}</Badge>
                <Badge variant="outline">{job.type}</Badge>
              </div>
              <div className="flex flex-wrap gap-4 text-gray-600">
                <div className="flex items-center space-x-1">
                  <MapPin className="w-4 h-4" />
                  <span>{job.location}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>Posted {formatPostedDate(job.createdDate)}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Briefcase className="w-4 h-4" />
                  <span>{job.type}</span>
                </div>
              </div> */}
            </div>

            {/* Description */}
            {/* <Card>
              <CardHeader>
                <CardTitle>Job Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">{job.description}</p>
              </CardContent>
            </Card> */}

            {/* Responsibilities */}
            {responsibilities.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Roles & Responsibilities</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {responsibilities.map((resp, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-600">{resp}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Qualifications */}
            {qualifications.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Minimum Experience</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {qualifications.map((qual, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-600">{qual}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Education */}
            <Card>
              <CardHeader>
                <CardTitle>Minimum Academic Qualification</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">{job.education || 'Not specified'}</p>
              </CardContent>
            </Card>
          </div>

          {/* Apply Form */}
          <div className="lg:col-span-1">
            <Card className="sticky top-20">
              <CardHeader>
                <CardTitle>Apply for this Position</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Basic Fields */}
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input id="name" name="name" value={formData.name} onChange={handleInputChange} required />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} required />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} required />
                  </div>

                  {/* Extra Fields */}
                  <div>
                    <Label htmlFor="yearsOfExperience">Years of Experience *</Label>
                    <select
                      id="yearsOfExperience"
                      name="yearsOfExperience"
                      className="w-full border rounded-md p-2 text-sm cursor-pointer"
                      value={formData.yearsOfExperience}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select...</option>
                      <option value="0-1">0-1 years</option>
                      <option value="1-3">1-3 years</option>
                      <option value="3-5">3-5 years</option>
                      <option value="5+">5+ years</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="education">Education *</Label>
                    <select
                      id="education"
                      name="education"
                      className="w-full border rounded-md p-2 text-sm cursor-pointer"
                      value={formData.education}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select...</option>
                      <option value="High School">High School</option>
                      <option value="Diploma">Diploma</option>
                      <option value="Bachelor's Degree">Bachelor's Degree</option>
                      <option value="Master's Degree">Master's Degree</option>
                      <option value="PhD">PhD</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="expectedPayPerDay">Expected Consultancy Fee (Per Day) *</Label>
                    <Input
                      id="expectedPayPerDay"
                      name="expectedPayPerDay"
                      type="number"
                      value={formData.expectedPayPerDay}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="expectedPayPerMonth">Expected Consultancy Fee (Per Month) *</Label>
                    <Input
                      id="expectedPayPerMonth"
                      name="expectedPayPerMonth"
                      type="number"
                      value={formData.expectedPayPerMonth}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="numbersOfProjectsWorked">Number of Projects Worked *</Label>
                    <select
                      id="numbersOfProjectsWorked"
                      name="numbersOfProjectsWorked"
                      className="w-full border rounded-md p-2 text-sm cursor-pointer"
                      value={formData.numbersOfProjectsWorked}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select...</option>
                      <option value="0-2">0-2 Projects</option>
                      <option value="3-5">3-5 Projects</option>
                      <option value="6-10">6-10 Projects</option>
                      <option value="10+">10+ Projects</option>
                    </select>
                  </div>

                  {/* Thematic Areas Section - Only for NON-Field Researcher positions */}
                  {!isFieldResearcher && (
                    <>
                      {/* Thematic Areas */}
                      <div>
                        <Label className="flex items-center gap-2">
                          Thematic Areas
                          <Badge variant="outline" className="text-xs">
                            Select up to 2 most relevant
                          </Badge>
                        </Label>
                        <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border rounded-md p-2">
                          {THEMATIC_AREAS.map(area => (
                            <label key={area} className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.selectedThematicAreas.includes(area)}
                                onChange={() => handleThematicAreaChange(area)}
                                className="rounded cursor-pointer"
                              />
                              <span className="text-sm">{area}</span>
                            </label>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {formData.selectedThematicAreas.length} selected
                        </p>
                      </div>

                      {/* Sub-Thematic Areas */}
                      {formData.selectedThematicAreas.length > 0 && (
                        <div>
                          <Label className="flex items-center gap-2">
                            Sub-Thematic Areas
                            <Badge variant="outline" className="text-xs">
                              Select up to 5 most relevant
                            </Badge>
                          </Label>
                          <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border rounded-md p-2">
                            {formData.selectedThematicAreas.flatMap(area =>
                              SUB_THEMATIC_AREAS[area]?.map(subArea => (
                                <label key={subArea} className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={formData.selectedSubThematicAreas.includes(subArea)}
                                    onChange={() => handleSubThematicAreaChange(subArea)}
                                    className="rounded cursor-pointer"
                                  />
                                  <span className="text-sm">{subArea}</span>
                                </label>
                              ))
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {formData.selectedSubThematicAreas.length} selected
                          </p>
                        </div>
                      )}

                      {/* Assignment Types */}
                      <div>
                        <Label className="flex items-center gap-2">
                          Assignment Types
                          <Badge variant="outline" className="text-xs">
                            Select up to 2 most relevant
                          </Badge>
                        </Label>
                        <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border rounded-md p-2">
                          {ASSIGNMENT_TYPES.map(type => (
                            <label key={type} className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.selectedAssignmentTypes.includes(type)}
                                onChange={() => handleAssignmentTypeChange(type)}
                                className="rounded cursor-pointer"
                              />
                              <span className="text-sm">{type}</span>
                            </label>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {formData.selectedAssignmentTypes.length} selected
                        </p>
                      </div>

                      {/* Sub-Assignment Types */}
                      {formData.selectedAssignmentTypes.length > 0 && (
                        <div>
                          <Label className="flex items-center gap-2">
                            Sub-Assignment Types
                            <Badge variant="outline" className="text-xs">
                              Select up to 5 most relevant
                            </Badge>
                          </Label>
                          <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border rounded-md p-2">
                            {formData.selectedAssignmentTypes.flatMap(type =>
                              SUB_ASSIGNMENT_TYPES[type]?.map(subType => (
                                <label key={subType} className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={formData.selectedSubAssignmentTypes.includes(subType)}
                                    onChange={() => handleSubAssignmentTypeChange(subType)}
                                    className="rounded cursor-pointer"
                                  />
                                  <span className="text-sm">{subType}</span>
                                </label>
                              ))
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {formData.selectedSubAssignmentTypes.length} selected
                          </p>
                        </div>
                      )}

                      {/* Cover Letter - Only for NON-Field Researcher positions */}
                      {/* <div>
                        <Label htmlFor="coverLetter">Cover Letter *</Label>
                        <Textarea
                          id="coverLetter"
                          name="coverLetter"
                          rows={6}
                          value={formData.coverLetter}
                          onChange={handleInputChange}
                          placeholder="Tell us why you're a great fit for this role..."
                          required
                        />
                      </div> */}
                    </>
                  )}

                  {/* Resume Upload Section */}
                  <div>
                    <Label>Resume/CV *</Label>
                    <div className="mt-2">
                      {!formData.resumeUrl ? (
                        <div className="relative">
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleFileChange}
                            disabled={uploading}
                            className="hidden"
                            id="resume"
                          />
                          <label
                            htmlFor="resume"
                            className={`block border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <div className="flex flex-col items-center justify-center gap-3">
                              <div className="p-2 bg-gray-100 rounded-full">
                                <Upload className="w-5 h-5 text-gray-500" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-700 mb-1">
                                  Click to upload your resume
                                </p>
                                <p className="text-xs text-gray-500">
                                  PDF, DOC, DOCX files. Max 5MB.
                                </p>
                              </div>
                              <div className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${uploading
                                ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                                : "bg-black text-white hover:bg-gray-800 cursor-pointer"
                                }`}>
                                <Upload className="w-4 h-4 mr-2" />
                                {uploading ? "Uploading..." : "Select Resume"}
                              </div>
                            </div>
                          </label>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-4">
                          <div className="flex flex-col sm:flex-row items-start gap-4">
                            <div className="relative flex-shrink-0">
                              <div className="w-full sm:w-56 h-40 border-2 border-gray-200 rounded-lg overflow-hidden flex items-center justify-center bg-gray-50">
                                <div className="text-center">
                                  <div className="p-3 bg-blue-100 rounded-full mx-auto mb-2 w-12 h-12 flex items-center justify-center">
                                    <span className="text-blue-600 font-bold text-sm">PDF</span>
                                  </div>
                                  <p className="text-xs text-gray-600 truncate max-w-[120px]">
                                    {resumeFile?.name || 'Resume File'}
                                  </p>
                                </div>
                              </div>
                            </div>

                            <div className="flex flex-col gap-3 flex-1">
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                onClick={handleDeleteResume}
                                disabled={uploading}
                                className="w-fit cursor-pointer"
                              >
                                <Trash className="w-4 h-4 mr-2" />
                                {uploading ? "Deleting..." : "Delete Resume"}
                              </Button>

                              <div className="flex items-center gap-2 text-sm">
                                {uploading && (
                                  <div className="flex items-center gap-2 text-blue-600">
                                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                                    Uploading resume...
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full cursor-pointer"
                    disabled={applyLoading || uploading || !formData.resumeUrl}
                  >
                    {applyLoading ? 'Submitting Application...' : uploading ? 'Uploading Resume...' : 'Submit Application'}
                  </Button>
                </form>

                <div className="mt-6 pt-6 border-t border-border">
                  <p className="text-xs text-gray-500">
                    By submitting this application, you agree to our Privacy Policy and consent to the
                    processing of your personal data for recruitment purposes.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}